import type { PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import type { SpecificationStreamContent, SpecificationStreamProvider } from '../types/specification';
import { create } from '@patsnap/tahiti-core';
import type { SSEResponse } from '@patsnap-utils/sse';
import { StreamStatus } from '@/services/sse-ps-http/enums';


function handleSSEMessage<D, R>(
    content: SSEResponse<SpecificationStreamContent<D>>,
    callback: (content: SpecificationStreamContent<R>) => void,
    deferred: ReturnType<typeof create>,
    dataFormatter: (data?: D) => R,
) {
    if (!content.data || content.data?.status === StreamStatus.FAILED) {
        callback({
            status: StreamStatus.FAILED,
            content: dataFormatter(content.data?.content),
        });
        deferred.reject(new Error(content.error_message));
    } else if (content.data?.status === StreamStatus.FINISH) {
        const data = {
            status: StreamStatus.FINISH,
            content: dataFormatter(content.data?.content),
        };
        callback(data);
    } else {
        callback({
            status: StreamStatus.PROCESSING,
            content: dataFormatter(content.data?.content),
        });
    }
}

/**
 * 将 PsHttpSSE 转换为 SpecificationStreamProvider, 增加易于理解的状态，方便业务端使用和监听
 */
export function SSE2StreamProvider<D, R>(
    sseService: PsHttpSSE<SpecificationStreamContent<D>>,
    dataFormatter: (data?: D) => R = (data) => data as unknown as R,
): SpecificationStreamProvider<SpecificationStreamContent<R>> {
    const deferred = create();
    let content: R = dataFormatter();
    const _dataFormatter = (data?: D) => {
        // 防止最后一次 FINISH 时，content 为空，导致数据丢失
        if (data) {
            content = dataFormatter(data);
        }
        return content;
    };
    return {
        subscribe: (callback) => {
            callback({ status: StreamStatus.PROGRESS_WITHOUT_DATA, content: _dataFormatter() });
            sseService.onMessage((content) => handleSSEMessage(content, callback, deferred, _dataFormatter));
            sseService.onFinish(() => {
                const data = { status: StreamStatus.FINISH, content: _dataFormatter() };
                callback(data);
                deferred.resolve(data);
            });
            sseService.onError((error) => {
                // 手动 close 也会走到这里，暂时不做是否为手动 close 的区分
                callback({ status: StreamStatus.FAILED, content: _dataFormatter() });
                deferred.reject(error);
            });
            sseService.start();
            return deferred;
        },
        abort: () => sseService.close(),
    };
}

export function http2StreamProvider<T, R = T>(
    api: (options: { single: AbortSignal }) => Promise<T>,
    dataFormatter: (data?: T) => R = (data) => data as unknown as R,
): SpecificationStreamProvider<SpecificationStreamContent<R>> {
    const deferred = create();
    const abortController = new AbortController();
    return {
        subscribe: (cb) => {
            cb({
                content: dataFormatter(),
                status: StreamStatus.PROGRESS_WITHOUT_DATA,
            });
            api({
                single: abortController.signal,
            }).then((response) => {
                const data = {
                    content: dataFormatter(response),
                    status: StreamStatus.FINISH,
                };
                cb(data);
                deferred.resolve(data);
            }).catch((error) => {
                cb({
                    content: dataFormatter(),
                    status: StreamStatus.FAILED,
                });
                deferred.reject(error);
            });
            return deferred;
        },
        abort: () => {
            abortController.abort();
        },
    };
}

// 安全地将内容转换为字符串，非字符串时返回空字符串
export function safeToString(content: unknown): string {
    return typeof content === 'string' ? content : '';
}
